import React from 'react'
import PropTypes from 'prop-types'
import { omit } from 'ramda'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { FormControl, Select, makeStyles } from '@material-ui/core'
import clsx from 'clsx'

import LabelError from 'app/componentV4/Labels/LabelError'
import Input from './Input'
import themesConfig from 'app/fuse-configs/themesConfig'

const useStyles = makeStyles((theme) => ({
  root: {
    fontSize: 14,
    '& .MuiSvgIcon-root': {
      fontSize: 20,
    },
  },
  menuList: {
    color: themesConfig.default.colors.primary,
    fontSize: 14,
  },
  menuItem: {
    '&:hover': {
      backgroundColor: theme.colors['primary-200'],
    },
    '&.Mui-selected': {
      fontWeight: 700,
      backgroundColor: theme.colors['primary-200'],
      '&:hover': {
        backgroundColor: theme.colors['primary-200'],
      },
    },
  },
}))

// Default props for Select dropdown behavior and styling
const defaultMenuProps = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: { style: { maxHeight: 300 } },
  MenuListProps: { className: null },
}

themesConfig.default.colors.primary // ensure import usage

const Selector = ({
  id,
  helperText,
  error,
  children,
  className,
  MenuProps: userMenuProps = {},
  ...rest
}) => {
  const classes = useStyles()

  // Strip out non-Select props
  const selectProps = omit(['helperText', 'InputProps', 'MenuProps'], rest)

  // Merge default menu props with user overrides
  const mergedMenuProps = {
    ...defaultMenuProps,
    ...userMenuProps,
    MenuListProps: {
      className: classes.menuList,
      ...(userMenuProps.MenuListProps || {}),
    },
  }

  // Enhance children with hover and selected styles
  const styledChildren = React.Children.map(children, (child) =>
    React.cloneElement(child, {
      className: clsx(child.props.className, classes.menuItem),
    }),
  )

  return (
    <FormControl fullWidth>
      <Select
        {...selectProps}
        input={<Input />}
        IconComponent={ExpandMoreIcon}
        className={clsx(classes.root, className)}
        MenuProps={mergedMenuProps}
      >
        {styledChildren}
      </Select>
      {error && <LabelError id={id} text={helperText} />}
    </FormControl>
  )
}

Selector.propTypes = {
  id: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  MenuProps: PropTypes.object,
}

Selector.defaultProps = {
  error: false,
  helperText: '',
  className: '',
  MenuProps: {},
}

export default Selector
